import React, { useCallback, useEffect, useState } from "react";
import { intl } from "LocaleWrapper";
import { useLazyQuery } from "@apollo/client";

import { QuickFilterTypes, Table } from "@cofano/cck";
import CreateIcon from "@mui/icons-material/Create";

import { useFrontendTable } from "components/table";

import { AdminLanguage, CustomerTranslations, CustomTranslationEntry } from "view/admin/utils/admin.types";
import LanguageEntryEditDialog from "view/admin/components/dialogs/LanguageEntryEditDialog";

import { CustomerLanguageSetDocument, GetAllLanguageSetsDocument } from "__generated__/view/admin/graphql/admin.queries.generated";

const AdminLanguageKeys = ({ languages }: { languages: AdminLanguage[] }) => {

	const [ dialog, setDialog ] = useState<{ open: boolean; entry: CustomTranslationEntry | null }>({
		open: false,
		entry: null,
	});

	const [ translationEntries, setTranslationEntries ] = useState<CustomTranslationEntry[]>([]);
	const [ customerTranslations, setCustomerTranslations ] = useState<CustomerTranslations>({ entries: {} });

	const [ getLanguageSets, { loading: languageSetsLoading } ] = useLazyQuery(GetAllLanguageSetsDocument);
	const [ getCustomerLanguageSet, { loading: customerLanguageSetLoading } ] = useLazyQuery(CustomerLanguageSetDocument);

	const loading = languageSetsLoading || customerLanguageSetLoading;

	// The Table memoizes row cells keyed on `id`, so it must change whenever a translation value changes,
	// otherwise an edited row keeps rendering its stale cell content until the page is reloaded.
	const generateRowId = (value: string): number => {
		let hash = 0;
		for (let i = 0; i < value.length; i++) {
			hash = (hash * 31 + value.charCodeAt(i)) | 0;
		}
		return hash;
	};

	const getTranslations = useCallback(() => {
		Promise.all([
			getLanguageSets(),
			getCustomerLanguageSet(),
		]).then(([ response1, response2 ]) => {

			const languageSets = response1.data?.languageSets ?? [];
			const customerLanguageSets = response2.data?.customerLanguageSet ?? [];
			const customerEntries: CustomerTranslations["entries"] = {};

			customerLanguageSets.forEach((result) => {
				if (result.key) {
					const valuesMap: Record<string, string> = {};
					result.translations?.forEach((value) => {
						if (value?.key) {
							valuesMap[value.key] = value.translation ?? "";
						}
					});
					customerEntries[result.key] = valuesMap;
				}
			});

			const keys: string[] = [];

			languageSets.forEach((languageSet) => {
				languageSet.translations?.forEach((value) => {
					if (value?.key && !keys.includes(value.key)) {
						keys.push(value.key);
					}
				});
			});

			Object.keys(customerEntries).forEach((key) => {
				if (!keys.includes(key)) {
					keys.push(key);
				}
			});

			keys.sort((a, b) => a.localeCompare(b));

			const currentTranslationEntries = keys.map((translationKey) => {

				const translations: CustomTranslationEntry["translations"] = {};

				languageSets.forEach((languageSet) => {
					const entry = languageSet.translations?.find((v) => v.key === translationKey);

					if (languageSet.language) {
						translations[languageSet.language.toLowerCase()] = entry?.translation ?? "";
					}
				});

				return {
					id: generateRowId(`${translationKey}::${JSON.stringify(translations)}`),
					key: translationKey,
					translations: translations,
				};
			});

			setTranslationEntries(currentTranslationEntries);
			setCustomerTranslations({ entries: customerEntries });
		});
	}, [ getLanguageSets, getCustomerLanguageSet ]);

	useEffect(() => {
		if (languages.length > 0) {
			getTranslations();
		}
	}, [ languages.length ]);

	const { tableProps } = useFrontendTable({
		id: "adminLanguageTable",
		defaultPageSize: 25,
		content: translationEntries,
		saveForOrganizationEnabled: false,
		onCustomFilter: (property: string) => {
			if (property !== "key") {
				return (field, value) => (field.toLowerCase()).includes(value.toLowerCase());
			}
		},
	});

	return (
		<React.Fragment>
			<Table
				{...tableProps}
				key={languages?.length}
				contentLoading={loading}
				columns={[ {
					id: "key",
					title: intl.formatMessage({ id: "KEY" }),
					filterComponent: QuickFilterTypes.TEXT,
					sortable: true,
				},
				...languages.map((language) => ({
					id: `translations.${language.code.toLowerCase()}`,
					title: language.description,
					render: (row: CustomTranslationEntry) => row.translations[language.code.toLowerCase()],
					filterComponent: QuickFilterTypes.TEXT,
				})),
				]}
				rowActions={[ {
					icon: <CreateIcon fontSize="small" />,
					onClick: (row: CustomTranslationEntry) => setDialog({ open: true, entry: row }),
				} ]} />

			{dialog.entry && (
				<LanguageEntryEditDialog
					open={dialog.open}
					languageEntry={dialog.entry}
					languages={languages}
					customerTranslations={customerTranslations}
					onClose={() => setDialog({ open: false, entry: null })}
					refetch={getTranslations}
				/>
			)}
		</React.Fragment>
	);
};

export default AdminLanguageKeys;
